import React from "react";
import {
  SlidersHorizontal,
  Inbox,
  Activity,
  ShieldCheck,
  Monitor,
  Headphones,
  Sparkles,
} from "lucide-react";

const controls = [
  {
    id: "configure",
    title: "Configure Intake",
    description: "Products, ticket sizes, cities, borrower profile. Change it any time.",
    icon: <SlidersHorizontal size={22} className="text-[var(--hero-green)]" strokeWidth={2.2} />,
  },
  {
    id: "lead-inbox",
    title: "Lead Inbox & Action",
    description: "Every file arrives verified. Accept, ask for documents, or pass in one click.",
    icon: <Inbox size={22} className="text-[var(--hero-green)]" strokeWidth={2.2} />,
  },
  {
    id: "dsa-performance",
    title: "DSA Performance View",
    description: "See conversion and quality history for the agent before you commit time.",
    icon: <Activity size={22} className="text-[var(--hero-green)]" strokeWidth={2.2} />,
  },
  {
    id: "risk-mitigation",
    title: "Risk Mitigation",
    description: "Timestamped KYC and policy pre-checks reduce underwriting risk at every step.",
    icon: <ShieldCheck size={22} className="text-[var(--hero-green)]" strokeWidth={2.2} />,
  },
  {
    id: "realtime-insights",
    title: "Real-time Insights",
    description: "Track pipeline stages, conversions, and portfolio growth in real-time.",
    icon: <Monitor size={22} className="text-[var(--hero-green)]" strokeWidth={2.2} />,
  },
  {
    id: "support",
    title: "Dedicated Support",
    description: "A dedicated relationship desk focused on your growth and lender SLAs.",
    icon: <Headphones size={22} className="text-[var(--hero-green)]" strokeWidth={2.2} />,
  },
];

function WhatYouControl() {
  return (
    <section id="what-you-control" className="relative overflow-hidden bg-white py-14 sm:py-18 lg:py-20">
      {/* Soft Ambient Radial Background Glow */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_50%_0%,rgba(46,229,162,0.06),transparent_50%)]"
      />

      <div className="section-container relative z-10">
        
        {/* ================= SECTION HEADER ================= */}
        <div className="mx-auto max-w-[740px] text-center">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-[var(--hero-green-light)] px-3.5 py-1 font-jakarta text-[12px] font-bold text-[var(--hero-green)] border border-[var(--hero-green-border)] shadow-xs mb-3.5">
            <Sparkles size={13} className="text-[var(--hero-green)]" />
            <span>BUILT FOR LENDERS</span>
          </span>

          <h2 className="font-bricolage text-[30px] sm:text-[38px] md:text-[44px] font-bold leading-[1.1] tracking-[-1px] text-[#111827]">
            Everything you need to <span className="text-[var(--hero-green)]">lend better</span>
          </h2>

          <p className="mt-2.5 font-jakarta text-[14.5px] sm:text-[16px] leading-[1.6] text-[#556369]">
            Our platform and network help you reduce costs, improve turnaround time, and grow your portfolio with high quality assets.
          </p>
        </div>

        {/* ================= 6 CONTROLS CARDS ROW GRID ================= */}
        <div className="mt-10 sm:mt-13 lg:mt-15 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3.5 sm:gap-4 lg:gap-4 xl:gap-5">
          {controls.map((item) => (
            <div
              key={item.id}
              className="group relative flex h-full flex-col items-center justify-between rounded-[18px] sm:rounded-[22px] border border-[#e5ebe7] bg-white p-4 sm:p-5 lg:p-5.5 text-center shadow-[0_3px_15px_rgba(0,0,0,0.02)] transition-all duration-300 hover:-translate-y-1 hover:border-[var(--hero-green-border)] hover:shadow-[0_14px_30px_rgba(0,135,81,0.08)]"
            >
              <div className="w-full">
                {/* Round Icon Badge - Guaranteed 1:1 Aspect Ratio Circle */}
                <div className="mx-auto flex h-12 w-12 sm:h-13 sm:w-13 lg:h-14 lg:w-14 shrink-0 aspect-square items-center justify-center rounded-full bg-[var(--hero-green-light)] border border-[var(--hero-green-border)] text-[var(--hero-green)] shadow-2xs transition-transform duration-300 group-hover:scale-108">
                  {item.icon}
                </div>

                {/* Title */}
                <h3 className="mt-3.5 sm:mt-4 font-bricolage text-[16px] sm:text-[17px] font-bold leading-snug tracking-tight text-[#111827] group-hover:text-[var(--hero-green)] transition-colors duration-200">
                  {item.title}
                </h3>

                {/* Description */}
                <p className="mt-1.5 font-jakarta text-[12.5px] sm:text-[13px] leading-[1.5] text-[#556369]">
                  {item.description}
                </p>
              </div>

              {/* Bottom Green Accent Indicator Dash */}
              <div className="mt-4 sm:mt-4.5 flex justify-center w-full">
                <div className="h-[2.5px] w-5 sm:w-6 rounded-full bg-[var(--hero-green)] transition-all duration-300 group-hover:w-9" />
              </div>
            </div>
          ))}
        </div>

      </div>
    </section>
  );
}

export default WhatYouControl;
